<template>
  <Teleport to="body">
    <!--
      ── La llamada con el asistente ────────────────────────────────────────
      Como una llamada de teléfono, a propósito: arriba quién y en qué estado
      (una línea), en medio lo que se dice como subtítulos discretos, abajo la
      tarjeta de lo que se prepara y la botonera de siempre — colgar en rojo,
      en el centro. Nada de paneles con rótulos: el estado se lee de un
      vistazo y el resto está en la tarjeta.

      Escribe en el MISMO store que el chat, así que colgar y seguir por
      escrito continúa justo donde estaba. Nunca arranca sola.
    -->
    <div class="call" role="dialog" aria-modal="true" :aria-label="t('assistant.call.title')">
      <!-- ── Terminada: cómo ha quedado ──────────────────────────────── -->
      <div v-if="phase === 'ended'" class="call__end">
        <span class="end-mark" :class="`end-mark--${ending.tone}`" aria-hidden="true">
          <svg v-if="ending.tone === 'ok'" viewBox="0 0 24 24"><path d="M5 12.5l4.2 4.2L19 7" /></svg>
          <svg v-else-if="ending.tone === 'failed'" viewBox="0 0 24 24"><path d="M7 7l10 10M17 7L7 17" /></svg>
          <svg v-else viewBox="0 0 24 24"><path d="M6 12h12" /></svg>
        </span>
        <p class="end-title">{{ ending.title }}</p>
        <p v-if="ending.detail" class="end-detail">{{ ending.detail }}</p>
        <p class="end-time mono">{{ clock }}</p>
        <div class="end-actions">
          <RouterLink v-if="ending.route" :to="ending.route" class="pill pill--primary" @click="emit('end', {})">{{ t('assistant.outcome.open') }}</RouterLink>
          <button type="button" class="pill" @click="emit('end', { toText: true })">{{ t('assistant.call.backToChat') }}</button>
        </div>
        <AssistantTestVerdict v-if="testing && store.conversationId" :conversation-id="store.conversationId" />
      </div>

      <template v-else>
        <!-- ── Quién y en qué estado ─────────────────────────────────────
             Tocar aquí mientras habla, la corta: es lo grande de la pantalla. -->
        <button
          type="button"
          class="caller"
          :disabled="phase !== 'speaking'"
          :aria-label="phase === 'speaking' ? t('assistant.call.tapToCut') : phaseLabel"
          @click="interrupt"
        >
          <span class="avatar" :class="`avatar--${phase}`" aria-hidden="true">
            <span class="avatar__ring" :style="{ transform: `scale(${1 + Math.min(level, 1) * 0.18})` }" />
            <svg class="avatar__icon" viewBox="0 0 24 24"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" /><path d="M18.5 15.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" /></svg>
          </span>
          <span class="caller__name">{{ t('assistant.call.title') }}</span>
          <span class="caller__status" role="status" aria-live="polite">
            <span class="status-dot" :class="`status-dot--${phase}`" aria-hidden="true" />
            {{ phase === 'speaking' ? t('assistant.call.tapToCut') : phaseLabel }}
            <span class="caller__sep" aria-hidden="true">·</span>
            <span class="mono">{{ clock }}</span>
          </span>
          <span v-if="testing" class="caller__rec">● {{ t('assistant.test.recording') }}</span>
        </button>

        <!-- ── Lo que se dice, como subtítulos ────────────────────────── -->
        <div class="captions">
          <p v-if="phase === 'offline'" class="notice notice--warn" role="status">
            {{ lastFile ? t('assistant.call.offlineKept') : t('assistant.call.offline') }}
          </p>
          <div v-else-if="phase === 'error'" class="notice notice--error" role="alert">
            <p>{{ problem }}</p>
            <p v-if="failure === 'mic'" class="notice__hint">{{ t(`assistant.call.micHint.${micHint}`) }}</p>
            <div class="notice__actions">
              <button type="button" class="pill pill--primary" @click="retry">{{ t('assistant.retry') }}</button>
              <button type="button" class="pill" @click="hangUp({ toText: true })">{{ t('assistant.call.toText') }}</button>
            </div>
          </div>
          <p v-else-if="notice" class="notice" role="status">{{ notice }}</p>

          <p v-if="heard" class="caption caption--you">«{{ heard }}»</p>
          <p v-if="reply" class="caption caption--reply">{{ reply }}</p>
          <p v-else-if="answer" class="caption">{{ answer }}</p>
        </div>

        <!-- ── Lo que se está preparando ──────────────────────────────── -->
        <div v-if="(store.pendingAction && store.pendingAction.complete !== false) || (store.widget && !store.sending)" class="sheet">
          <AssistantProposal
            v-if="store.pendingAction && store.pendingAction.complete !== false"
            :key="store.pendingAction.id"
            :action="store.pendingAction"
            :hint="t('assistant.call.sayYes')"
            compact
            @confirm="touch('yes')"
            @reject="touch('no')"
          />
          <AssistantAsk v-else :key="store.widget.id" :widget="store.widget" />
        </div>

        <!-- ── La botonera de una llamada ─────────────────────────────── -->
        <footer class="keys">
          <button
            v-if="phase === 'speaking'"
            type="button" class="key" @click="interrupt"
          >
            <span class="key__face" aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="7" y="7" width="10" height="10" rx="1.5" /></svg>
            </span>
            <span class="key__label">{{ t('assistant.call.interrupt') }}</span>
          </button>
          <button
            v-else
            type="button" class="key" :class="{ 'key--on': paused }"
            :disabled="phase === 'error' || phase === 'offline'"
            :aria-pressed="paused"
            @click="togglePause"
          >
            <span class="key__face" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M12 15a3 3 0 003-3V6a3 3 0 00-6 0v6a3 3 0 003 3z" /><path d="M18 11a6 6 0 01-12 0M12 17v3" /><path v-if="paused" d="M4 4l16 16" /></svg>
            </span>
            <span class="key__label">{{ paused ? t('assistant.call.resume') : t('assistant.call.pause') }}</span>
          </button>

          <button type="button" class="key key--end" @click="hangUp()">
            <span class="key__face" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M3.5 14.5c4.7-4.2 12.3-4.2 17 0l-2.2 2.4-3.3-1.4v-2.3a10 10 0 00-6 0v2.3l-3.3 1.4z" /></svg>
            </span>
            <span class="key__label">{{ t('assistant.call.hangUp') }}</span>
          </button>

          <button type="button" class="key" @click="hangUp({ toText: true })">
            <span class="key__face" aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10" /></svg>
            </span>
            <span class="key__label">{{ t('assistant.call.toText') }}</span>
          </button>
        </footer>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, ApiError, useSpeech, useVoiceTurn, errorMessage } from '@general/ui-kit'

import { useAssistantStore } from '../store'
import { collectContext } from '../context'
import { phraseOf, readBackOf, understoodOf, micPlatform, fixedPhrases, createHold, HOLD_KEYS } from '../callText'
import AssistantProposal from './AssistantProposal.vue'
import AssistantAsk from './AssistantAsk.vue'
import AssistantTestVerdict from './AssistantTestVerdict.vue'

const emit = defineEmits(['end'])

const { t, te } = useI18n()
const store = useAssistantStore()
const voice = useVoiceTurn()
const speech = useSpeech()

/** Ver callText.PHASES. 'processing' es lo recién dicho, de camino al servidor. */
const phase = ref('connecting')
const level = computed(() => (phase.value === 'listening' ? voice.level.value : 0))
const heard = ref('')
const answer = ref('')
const reply = ref('')
const problem = ref('')
const notice = ref('')
const paused = ref(false)
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false)
const failure = ref(null)
const micHint = micPlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent)

let active = true
// Lo tocado en pantalla mientras se escuchaba: corta el turno y se atiende.
let tapped = null
// Turnos seguidos sin que nadie hable. Al segundo se pregunta; al tercero se cuelga.
let misses = 0
// Lo último que dijo el teléfono: con el altavoz, el micro lo vuelve a oír al
// principio de la respuesta («¿Qué hay que hacer? Mira, tendríamos…»), y el
// servidor lo quita (ver `lastSaid` en /call/turn).
let lastSaid = ''
// El modo prueba (recorder.js): solo si el servidor lo tiene encendido para
// esta persona. Se ve durante toda la llamada, y al colgar pide el veredicto.
const testing = ref(false)
api.get('/assistant/test-recording').then((status) => { testing.value = Boolean(status?.enabled) }).catch(() => {})

/** El aparato y el navegador, para una llamada de prueba. */
function deviceInfo(file) {
  const nav = typeof navigator === 'undefined' ? {} : navigator
  return {
    platform: nav.userAgentData?.platform ?? nav.platform ?? '',
    mobile: nav.userAgentData?.mobile ?? /Mobi|Android|iPhone/i.test(nav.userAgent ?? ''),
    brands: (nav.userAgentData?.brands ?? []).map(brand => `${brand.brand} ${brand.version}`).join(', '),
    screen: typeof screen === 'undefined' ? '' : `${screen.width}x${screen.height}@${window.devicePixelRatio ?? 1}`,
    language: nav.language ?? '',
    recorded: file?.type ?? '',
  }
}
// Lo último grabado, para reintentar sin pedir que lo repita (también sin red).
const lastFile = ref(null)
let resumeCall = null

const lang = () => document.documentElement.lang || 'es'
const locale = () => lang().slice(0, 2)
// Sin conexión, la etiqueta es la corta: la explicación va en el aviso de debajo.
const phaseLabel = computed(() => t(phase.value === 'offline' ? 'assistant.call.offlineBadge' : `assistant.call.${phase.value}`))
const understood = computed(() => (store.pendingAction ? understoodOf(store.pendingAction, { locale: lang() }) : ''))

// ── El reloj de la llamada ─────────────────────────────────────────────────
const startedAt = Date.now()
const now = ref(Date.now())
const ticker = setInterval(() => { now.value = Date.now() }, 1000)
const clock = computed(() => {
  const seconds = Math.floor((now.value - startedAt) / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
})

// ── Hablar ─────────────────────────────────────────────────────────────────

// Las frases propias de la llamada, ya sintetizadas: «Dime» suena igual en
// cada llamada y no se vuelve a pedir. Se piden todas al empezar (prefetch),
// en paralelo con la prueba del micrófono, para que suenen al instante.
const voiced = new Map()

async function voiceOf(text) {
  if (voiced.has(text)) return voiced.get(text)
  const pending = api.post('/assistant/call/say', { text, locale: locale(), actionId: store.actionId || undefined })
    .then(result => result.speech ?? null)
    .catch(() => null)
  voiced.set(text, pending)
  const speechOf = await pending
  if (!speechOf) voiced.delete(text)
  return speechOf
}

function prefetch() {
  if (!store.capabilities?.canSpeak || !online.value) return
  for (const text of fixedPhrases(t)) voiceOf(text)
}

/**
 * Una frase de la llamada, con la MISMA voz que las respuestas cuando la
 * instancia tiene una; si no, o si falla, con la del navegador.
 */
async function sayAloud(text) {
  if (!text || !active) return
  lastSaid = text
  if (store.capabilities?.canSpeak && online.value) {
    const speechOf = await voiceOf(text)
    if (speechOf?.audio && active) {
      await speech.play(speechOf.audio, { mimeType: speechOf.mimeType })
      return
    }
  }
  return browserSay(text)
}

/** Con la voz del navegador, esperando a que acabe (speak() no lo dice). */
function browserSay(text) {
  if (!text || !active) return Promise.resolve()
  speech.speak(text, { locale: locale() })
  return new Promise((resolve) => {
    const limit = setTimeout(done, 20_000)
    const started = setTimeout(() => { if (!speech.speaking.value) done() }, 600)
    const stop = watch(speech.speaking, (talking) => { if (!talking) done() })
    function done() { clearTimeout(limit); clearTimeout(started); stop(); resolve() }
  })
}

async function speak(result) {
  if (!active) return
  phase.value = 'speaking'
  if (result?.speech?.audio) {
    lastSaid = result.spoken || lastSaid
    await speech.play(result.speech.audio, { mimeType: result.speech.mimeType })
  } else {
    await sayAloud(result?.spoken || phraseOf(result?.reply, { t, te }) || outcomeSaid(result?.outcome)
      || (result?.cancelled ? t('assistant.call.cancelled') : '')
      || (result?.pendingAction ? readBackOf(result.pendingAction, { t, locale: lang() }) : ''))
  }
}

/** El resultado real dicho en voz alta, cuando el modelo no pudo decirlo. */
function outcomeSaid(outcome) {
  if (!outcome) return ''
  if (!outcome.ok) return t('assistant.outcome.failed')
  return phraseOf(outcome.text, { t, te }) || t('assistant.outcome.done')
}

/** Cortarle: calla y vuelve a escuchar. Lo que estaba diciendo sigue escrito. */
function interrupt() {
  if (phase.value === 'speaking') speech.stop()
}

// ── «Un momento, lo miro» (callText.createHold) ─────────────────────────────
// Con la misma voz y ya sintetizada al descolgar (prefetch): no cuesta una
// síntesis por turno.
const hold = createHold({ say: text => sayAloud(text), phrases: HOLD_KEYS.map(key => t(key)) })

// ── Un turno ───────────────────────────────────────────────────────────────

// El turno en curso, para cortarlo al colgar: leído a trozos no tiene el
// tiempo límite de una petición normal, así que lleva el suyo.
let turnController = null
const TURN_TIMEOUT_MS = 90_000

/**
 * El turno leído a trozos (POST /call/turn con event-stream): `heard` en
 * cuanto el servidor lo ha oído —se enseña ya y arma el «un momento»—, `tool`
 * cuando el agente consulta algo —lo dice ya— y `done` con lo de siempre.
 */
async function send(file) {
  const form = new FormData()
  form.append('audio', file, file.name ?? 'turn.webm')
  form.append('locale', locale())
  if (store.conversationId) form.append('conversationId', store.conversationId)
  // Sin `pendingId`: un «sí» va como palabras y el servidor sabe a qué
  // contesta. Mandarlo confirmaba la tarjeta aunque siguiera preguntando.
  if (lastSaid) form.append('lastSaid', lastSaid.slice(0, 500))
  if (testing.value) form.append('device', JSON.stringify(deviceInfo(file)))
  try { form.append('screenContext', JSON.stringify(collectContext())) } catch { /* sin contexto */ }

  turnController = new AbortController()
  const controller = turnController
  const timer = setTimeout(() => controller.abort(), TURN_TIMEOUT_MS)
  let finished = null
  try {
    await api.stream('/assistant/call/turn', form, {
      signal: controller.signal,
      onEvent: (event) => {
        if (event.type === 'heard') {
          heard.value = event.transcript ?? ''
          phase.value = 'thinking'
          hold.heard()
        } else if (event.type === 'tool') {
          hold.busy()
        } else if (event.type === 'done') {
          finished = event
        } else if (event.type === 'error') {
          throw new ApiError(event.message, 500, event.code, { details: event.details, requestId: event.requestId })
        }
      },
    })
  } catch (error) {
    // Cortado por el reloj, no por colgar: como el tiempo límite de antes.
    if (error?.code === 'ABORTED' && active) throw new ApiError('The request took too long. Try again.', 0, 'TIMEOUT')
    throw error
  } finally {
    clearTimeout(timer)
    if (turnController === controller) turnController = null
  }
  if (!finished) throw new ApiError('The call turn ended without an answer', 0, 'NETWORK_ERROR')
  const { type, ...result } = finished
  return result
}

async function handle(file) {
  problem.value = ''
  notice.value = ''
  // Sin red no se intenta: se guarda y sale solo al volver (ver `online`).
  if (!online.value) {
    lastFile.value = file
    phase.value = 'offline'
    return
  }
  // Al momento, antes de la respuesta: lo dicho ya se está tratando.
  phase.value = 'processing'
  hold.begin()
  let result
  try {
    result = await send(file)
  } catch (error) {
    await hold.settle()
    // Colgó mientras se contestaba: el turno se cortó a propósito.
    if (!active) return
    // Sin respuesta del servidor: lo grabado se guarda para reintentar, y si
    // era un «sí» el servidor contesta lo mismo — no crea dos veces.
    lastFile.value = file
    if (!online.value || error?.code === 'NETWORK_ERROR') {
      phase.value = 'offline'
      return
    }
    failure.value = 'network'
    problem.value = `${t('assistant.call.networkLost')} ${errorMessage(error, t)}`
    phase.value = 'error'
    return
  }
  lastFile.value = null
  // La respuesta espera a que acabe el «un momento», si sonaba: no se pisan.
  await hold.settle()
  if (!active) return
  if (!result.transcript) {
    notice.value = t('assistant.call.heardNothing')
    return
  }
  heard.value = result.transcript
  answer.value = result.answer ?? ''
  reply.value = phraseOf(result.reply, { t, te })
  store.applyCallTurn(result)
  await speak(result)
}

/** Espera a que el store termine lo que tiene en marcha. */
const settled = () => new Promise((resolve) => {
  if (!store.sending) return resolve()
  const stop = watch(() => store.sending, (busy) => { if (!busy) { stop(); resolve() } })
})

/**
 * Lo tocado en la tarjeta o el widget: lo mismo que dicho, por el store.
 * 'store' es algo que ya puso en marcha otro componente (un widget
 * contestado): solo se espera su respuesta.
 */
async function attendTouch(choice) {
  phase.value = 'thinking'
  const before = choice === 'store' ? Math.max(0, store.messages.length - 1) : store.messages.length
  if (choice === 'yes') await store.confirmAction()
  else if (choice === 'no') store.rejectAction()
  else await settled()
  if (store.error) {
    failure.value = 'touch'
    problem.value = errorMessage(store.error, t)
    phase.value = 'error'
    return
  }
  const said = store.messages.slice(before).reverse().find(message => message.role === 'assistant' && (message.text || message.reply || message.outcome))
  const text = said ? (said.text || phraseOf(said.reply, { t, te }) || outcomeSaid(said.outcome)) : (choice === 'no' ? t('assistant.call.cancelled') : '')
  if (text) {
    answer.value = said?.text ?? ''
    reply.value = said?.reply ? phraseOf(said.reply, { t, te }) : ''
    phase.value = 'speaking'
    await sayAloud(text)
  }
}

async function loop() {
  phase.value = 'connecting'
  // Pedir el micro una vez antes de hablar —si lo deniega, se sabe ya y no a
  // mitad de la primera frase— mientras se piden las voces de las frases fijas.
  prefetch()
  try {
    await voice.listen({ filename: 'probe.webm', patienceMs: 1, maxSeconds: 1 })
  } catch (error) {
    micProblem(error)
    return
  }
  phase.value = 'speaking'
  await sayAloud(t('assistant.call.greeting'))

  while (active) {
    if (paused.value) {
      phase.value = 'paused'
      await new Promise((resolve) => { resumeCall = resolve })
      continue
    }
    if (phase.value === 'error' || phase.value === 'offline') return

    phase.value = 'listening'
    let file = null
    try {
      file = await voice.listen({ filename: 'turn.webm' })
    } catch (error) {
      micProblem(error)
      return
    }
    if (!active) break

    if (tapped) {
      const choice = tapped
      tapped = null
      await attendTouch(choice)
      continue
    }
    if (paused.value) continue

    if (!file) {
      misses += 1
      if (misses === 2) { phase.value = 'speaking'; await sayAloud(t('assistant.call.stillThere')) }
      if (misses >= 3) { await hangUp({ farewell: t('assistant.call.bye') }); break }
      continue
    }
    misses = 0
    await handle(file)
  }
}

function micProblem(error) {
  failure.value = 'mic'
  problem.value = (error?.reason && voice.problemLabel?.(error.reason)) || t('assistant.call.noMicrophone')
  phase.value = 'error'
}

async function retry() {
  const what = failure.value
  failure.value = null
  problem.value = ''
  if ((what === 'network' || phase.value === 'offline') && lastFile.value) {
    await handle(lastFile.value)
    if (phase.value !== 'error' && phase.value !== 'offline') loop()
    return
  }
  if (what === 'touch') store.error = null
  loop()
}

function touch(choice) {
  if (phase.value === 'listening') {
    tapped = choice
    // Corta la escucha en curso: el bucle atiende el toque en cuanto vuelve.
    voice.cancel()
  } else if (phase.value === 'paused' || phase.value === 'speaking') {
    speech.stop()
    attendTouch(choice)
  }
}

// Un widget contestado tocando mientras se escucha: se corta la escucha (lo
// grabado no se envía) y la llamada espera la respuesta para leerla.
watch(() => store.sending, (busy) => {
  if (!busy || !active || phase.value !== 'listening' || tapped) return
  tapped = 'store'
  voice.cancel()
})

function togglePause() {
  paused.value = !paused.value
  if (paused.value) { voice.cancel(); speech.stop() } else { resumeCall?.(); resumeCall = null }
}

// ── La red ─────────────────────────────────────────────────────────────────
// Sin red la llamada no se rompe: lo dicho se queda y sale solo al volver.
function wentOffline() {
  online.value = false
  if (phase.value === 'listening' || phase.value === 'processing') {
    voice.cancel()
    if (phase.value !== 'processing') phase.value = 'offline'
  }
}

async function cameBack() {
  online.value = true
  if (!active || phase.value !== 'offline') return
  if (lastFile.value) await handle(lastFile.value)
  if (active && phase.value !== 'error' && phase.value !== 'offline') loop()
}

// ── Colgar, y cómo ha quedado ──────────────────────────────────────────────

const ending = computed(() => {
  const done = [...store.messages].reverse().find(message => message.outcome)
  if (done?.outcome?.ok) {
    return {
      tone: 'ok',
      icon: '✓',
      title: phraseOf(done.outcome.text, { t, te }) || t('assistant.outcome.done'),
      route: done.outcome.route,
    }
  }
  if (store.pendingAction) return { tone: 'pending', icon: '…', title: t('assistant.call.endedPending'), detail: t('assistant.call.endedPendingDetail') }
  if (store.widget) return { tone: 'pending', icon: '?', title: t('assistant.call.endedMissing'), detail: t('assistant.call.endedPendingDetail') }
  if (done && !done.outcome.ok) {
    const { error, errorCode, errorDetails } = done.outcome
    const detail = errorCode ? errorMessage({ code: errorCode, details: errorDetails ?? null, message: error ?? '' }, t) : (error ?? '')
    return { tone: 'failed', icon: '×', title: t('assistant.outcome.failed'), detail }
  }
  return { tone: 'none', icon: '·', title: t('assistant.call.endedNothing') }
})

async function hangUp({ toText = false, farewell = '' } = {}) {
  if (!active && phase.value === 'ended') return
  active = false
  voice.cancel()
  speech.stop()
  // Lo que estaba contestando ya no lo va a oír nadie: se corta también en el
  // servidor (y en el proveedor), como el botón de parar del panel.
  turnController?.abort()
  resumeCall?.()
  if (farewell) { active = true; await sayAloud(farewell); active = false }
  if (toText) { emit('end', { toText: true }); return }
  phase.value = 'ended'
}

onMounted(() => {
  window.addEventListener('offline', wentOffline)
  window.addEventListener('online', cameBack)
  if (!voice.supported) {
    failure.value = 'mic'
    problem.value = t('assistant.call.noMicrophone')
    phase.value = 'error'
    return
  }
  loop()
})

onBeforeUnmount(() => {
  active = false
  clearInterval(ticker)
  window.removeEventListener('offline', wentOffline)
  window.removeEventListener('online', cameBack)
  voice.cancel()
  speech.stop()
  turnController?.abort()
})
</script>

<style scoped>
.caller__rec { display: block; margin-top: 4px; font-size: 12px; color: var(--color-danger); letter-spacing: .02em; }
.call {
  /* Del tema de quien llama (Claro / Oscuro / Auto), como el resto del ERP:
     la pantalla de llamada era siempre oscura y, con el tema claro, saltaba
     de un ERP blanco a una pantalla negra. Los colores con significado —verde
     hablando, azul escuchando, rojo colgar— son los mismos en los dos. */
  --call-bg: var(--color-bg);
  --call-bg-2: var(--color-surface);
  --call-text: var(--color-text);
  --call-soft: var(--color-text-soft);
  --call-line: var(--color-border);
  --call-key: var(--color-surface-2);
  --call-key-on: var(--color-text);
  position: fixed;
  inset: 0;
  z-index: 190;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, var(--call-bg-2) 0%, var(--call-bg) 60%);
  color: var(--call-text);
  padding: calc(env(safe-area-inset-top) + 28px) 16px calc(env(safe-area-inset-bottom) + 20px);
  font-family: inherit;
}

/* ── Quién y en qué estado ─────────────────────────────────────────────── */
.caller {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 0 auto;
  padding: 8px 16px;
  background: none;
  border: 0;
  color: inherit;
  font: inherit;
  cursor: default;
}
.caller:not(:disabled) { cursor: pointer; }
.caller__name { font-size: 1.5rem; font-weight: 600; letter-spacing: -0.01em; margin-top: 10px; }
.caller__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  color: var(--call-soft);
}
.caller__sep { opacity: 0.6; }

.avatar {
  position: relative;
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
}
.avatar__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid var(--color-primary, #2563eb);
  opacity: 0;
  transition: transform 120ms ease-out, opacity 200ms ease;
}
.avatar--listening .avatar__ring { opacity: 0.85; }
.avatar--speaking .avatar__ring { opacity: 0.85; border-color: var(--color-success, #22c55e); animation: ring 1.4s ease-in-out infinite; }
.avatar--processing .avatar__ring, .avatar--thinking .avatar__ring { opacity: 0.6; border-style: dashed; animation: spin 2.4s linear infinite; }
.avatar__icon {
  width: 84px;
  height: 84px;
  padding: 22px;
  border-radius: 50%;
  background: linear-gradient(160deg, var(--color-primary-dark, #1e3a8a), var(--color-primary, #1d4ed8));
  fill: var(--color-primary-soft, #e0e7ff);
  stroke: none;
  box-shadow: 0 6px 24px rgba(29, 78, 216, 0.35);
}
@keyframes ring { 50% { transform: scale(1.06); } }
@keyframes spin { to { transform: rotate(360deg); } }

.status-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--call-soft); flex: none; }
.status-dot--listening { background: var(--color-info, #3b82f6); }
.status-dot--speaking { background: var(--color-success, #22c55e); }
.status-dot--processing, .status-dot--thinking, .status-dot--connecting { background: var(--color-warning, #eab308); }
.status-dot--error, .status-dot--offline { background: var(--color-danger, #ef4444); }
.status-dot--paused { background: var(--call-soft); }

/* ── Subtítulos ─────────────────────────────────────────────────────────── */
.captions {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  max-width: 520px;
  margin: 18px auto 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  text-align: center;
}
.caption { margin: 0; font-size: 1.05rem; line-height: 1.45; color: var(--call-text); }
.caption--you { font-size: 0.95rem; color: var(--call-soft); font-style: italic; }
.caption--reply { font-size: 1.2rem; font-weight: 500; }

.notice {
  margin: 0;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--call-key);
  color: var(--call-text);
  font-size: 0.92rem;
  text-align: left;
}
.notice p { margin: 0; }
.notice--warn { border-left: 3px solid var(--color-warning, #eab308); }
.notice--error { border-left: 3px solid var(--color-danger, #ef4444); display: flex; flex-direction: column; gap: 8px; }
.notice__hint { color: var(--call-soft); font-size: 0.85rem; }
.notice__actions { display: flex; gap: 8px; }

/* ── La tarjeta ─────────────────────────────────────────────────────────── */
.sheet {
  width: 100%;
  max-width: 520px;
  margin: 14px auto 0;
  max-height: 46vh;
  overflow-y: auto;
  border-radius: 16px;
  color: var(--color-text);
  border: 1px solid var(--call-line);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
}

/* ── La botonera ────────────────────────────────────────────────────────── */
.keys {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 36px;
  padding-top: 22px;
}
.key {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  min-width: 72px;
}
.key:disabled { opacity: 0.35; cursor: default; }
.key__face {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--call-key);
  transition: background 120ms ease, transform 80ms ease;
}
.key:active:not(:disabled) .key__face { transform: scale(0.95); }
.key__face svg { width: 26px; height: 26px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.key--on .key__face { background: var(--call-key-on); color: var(--call-bg); }
.key--end .key__face { background: var(--color-danger, #dc2626); color: #fff; }
.key--end .key__face svg { fill: currentColor; stroke: none; width: 30px; height: 30px; }
.key__label { font-size: 0.78rem; color: var(--call-soft); }

/* ── Terminada ──────────────────────────────────────────────────────────── */
.call__end {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}
.end-mark {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--call-key);
}
.end-mark svg { width: 34px; height: 34px; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
.end-mark--ok { background: var(--color-success-soft, rgba(34, 197, 94, 0.16)); color: var(--color-success, #22c55e); }
.end-mark--failed { background: var(--color-danger-soft, rgba(239, 68, 68, 0.16)); color: var(--color-danger, #ef4444); }
.end-title { margin: 6px 0 0; font-size: 1.25rem; font-weight: 600; }
.end-detail { margin: 0; color: var(--call-soft); max-width: 360px; }
.end-time { margin: 0; color: var(--call-soft); font-size: 0.9rem; }
.end-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 14px; }

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid var(--call-line);
  background: var(--call-key);
  color: var(--call-text);
  font: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}
.pill--primary { background: var(--color-primary, #2563eb); border-color: transparent; color: #fff; }

@media (prefers-reduced-motion: reduce) {
  .avatar__ring { animation: none !important; transition: none; }
}
</style>
